import { put, takeLatest } from "@redux-saga/core/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { call } from "redux-saga/effects";
import fetchAPI, { ApiResponse, HttpMethod } from "../../../utils/api";
import { showError } from "../../toaster/store/slice";
import documentSlice, { Media } from "./slice";

type DocumentFetchResponse = ApiResponse<{ data: Media[] }>;
type DocumentStoreResponse = ApiResponse<{ data: Media[] }>;
type DocumentDeleteResponse = ApiResponse<{ success: boolean }>;
type DocumentDownloadResponse = Blob | { message: string };

function* documentSaga() {
  yield takeLatest(documentSlice.actions.request, handleRequest);
  yield takeLatest(documentSlice.actions.upload, handleUpload);
  yield takeLatest(documentSlice.actions.delete, handleDelete);
  yield takeLatest(documentSlice.actions.download, handleDownload);
}

function* handleRequest({ payload: bookingId }: PayloadAction<number>) {
  const response: DocumentFetchResponse = yield call(
    fetchAPI,
    `/documents?booking_id=${bookingId}`
  );

  if (response && response.data) {
    yield put(documentSlice.actions.addData(response.data));
  }

  yield put(documentSlice.actions.setIsLoading(false));
}

function* handleUpload({ payload: files }: PayloadAction<File[]>) {
  const body = new FormData();

  files.forEach((file, index) => {
    body.append(`attachments[${index}]`, file);
  });

  const response: DocumentStoreResponse = yield call(fetchAPI, `/documents`, {
    method: HttpMethod.POST,
    body,
    multipart: true,
  });

  if (response.data) {
    yield put(documentSlice.actions.addData(response.data));
  }

  yield put(documentSlice.actions.setIsLoading(false));
}

function* handleDelete({ payload: id }: PayloadAction<number>) {
  const response: DocumentDeleteResponse = yield call(
    fetchAPI,
    `/documents/${id}`,
    {
      method: HttpMethod.DELETE,
    }
  );

  if (response.success) {
    yield put(documentSlice.actions.removeData(id));
  }

  yield put(documentSlice.actions.setIsLoading(false));
}

function* handleDownload({ payload: document }: PayloadAction<Media>) {
  try {
    const response: DocumentDownloadResponse = yield call(
      fetchAPI,
      `/documents/${document.id}/download`
    );

    if (response && response instanceof Blob) {
      const link = window.document.createElement("a");

      link.href = window.URL.createObjectURL(response);
      link.download = document.name;

      link.dispatchEvent(new MouseEvent("click"));
    } else {
      yield put(showError(response.message));
    }
  } catch (error) {
    yield put(showError("File download failed."));
  }

  yield put(documentSlice.actions.setIsLoading(false));
}

export default documentSaga;
